/**
 * LegacyExtractorPane — v2.12.8
 *
 * Turns proprietary desktop database files (.dbf, .mdb, .accdb, .sqlite,
 * MySQL/PG SQL dumps, etc.) into a per-table CSV bundle that Migration
 * Studio can then profile + map like any other data source.
 *
 * Two-step wizard:
 *   1. Detect     — sniff magic bytes; report format + whether Konnect can
 *                   auto-extract or needs a guided vendor tool.
 *   2. Extract    — auto-extract every table to CSV. Optionally auto-import
 *                   into the currently-active Migration Project.
 *
 * Wizard scripts are surfaced verbatim for formats we can't natively handle
 * (SAP SQL Anywhere, pg_dump binary, QuickBooks, etc.).
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Loader2, Sparkles, FileWarning, CheckCircle2, Download,
  Database, ArrowRight, Copy, AlertTriangle, ArrowUpRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMAT_ICONS = {
  dbase: '📇', sqlite: '🗄️', access: '📊', mysql_dump: '🐬',
  pg_dump_plain: '🐘', pg_dump_custom: '🐘', sql_anywhere: '🏛️',
  xls_legacy: '📈', unknown: '❔',
};

export default function LegacyExtractorPane({ activeProject, onExtractImported }) {
  const [file, setFile] = useState(null);
  const [detection, setDetection] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extractResult, setExtractResult] = useState(null);

  const handleFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setDetection(null);
    setExtractResult(null);
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await axios.post(`${API}/api/migration/legacy-detect`, form);
      setDetection(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Detection failed');
    } finally { setBusy(false); }
  };

  const doExtract = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/api/migration/legacy-extract`, form);
      setExtractResult(data);
      toast.success(`Extracted ${data.total_files} table(s), ${data.total_rows.toLocaleString()} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Extraction failed');
    } finally { setBusy(false); }
  };

  const importIntoProject = async () => {
    if (!extractResult || !activeProject) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/migration/projects/${activeProject}/legacy-import`,
        { extract_id: extractResult.extract_id, project_id: activeProject },
      );
      toast.success(`Registered ${data.count} data source(s) in project`);
      setExtractResult(null);
      setFile(null);
      setDetection(null);
      onExtractImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="legacy-extractor-pane">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <CardTitle className="text-base sm:text-lg">Legacy Extractor</CardTitle>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">v2.12.8 · NEW</Badge>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Turn old <b>.dbf</b> · <b>.mdb</b> · <b>.accdb</b> · <b>.sqlite</b> · <b>MySQL/Postgres dumps</b> into clean
          per-table CSVs. Auto-detects the format from magic bytes. Guided-wizard fallback for SAP SQL Anywhere,
          binary <code className="text-[10px] px-1 bg-slate-100 rounded">pg_dump</code>, and other proprietary formats.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Step 1 — File pick */}
        <label
          data-testid="legacy-extractor-file-input-label"
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
            file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-300 hover:border-emerald-400 hover:bg-slate-50'
          }`}
        >
          <Upload className="w-6 h-6 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">
            {file ? file.name : 'Drop a legacy database file (or click to pick)'}
          </span>
          <span className="text-[11px] text-slate-500">
            {file ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : 'Max 200 MB (or your tenant override)'}
          </span>
          <input
            type="file"
            className="hidden"
            onChange={handleFilePick}
            accept=".dbf,.mdb,.accdb,.sqlite,.db,.db3,.sql,.dump,.xls"
            data-testid="legacy-extractor-file-input"
          />
        </label>

        {busy && !detection && (
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sniffing magic bytes…
          </div>
        )}

        {/* Step 2 — Detection result */}
        {detection && (
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2" data-testid="detection-result">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl">{FORMAT_ICONS[detection.format] || '📁'}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-slate-900">{detection.display_name}</div>
                <div className="text-[11px] text-slate-500">
                  Format · <code className="text-[10px] px-1 bg-white rounded">{detection.format}</code>
                  {' · '}Confidence · {(detection.confidence * 100).toFixed(0)}%
                </div>
              </div>
              {detection.extractable ? (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Auto-extract
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                  <FileWarning className="w-3 h-3 mr-1" /> Guided wizard
                </Badge>
              )}
            </div>
            <div className="text-xs text-slate-600">{detection.notes}</div>

            {/* Guided wizard script when not auto-extractable */}
            {!detection.extractable && detection.wizard_script && (
              <div className="mt-2 space-y-1.5">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-[11px] font-medium text-amber-800">
                    Copy this script into a terminal after installing SAP SQL Anywhere Developer Edition (free):
                  </span>
                </div>
                <pre className="text-[10px] leading-relaxed bg-slate-900 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre max-h-64">
                  {detection.wizard_script}
                </pre>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => { navigator.clipboard.writeText(detection.wizard_script); toast.success('Script copied'); }}
                    data-testid="wizard-copy-btn"
                    className="text-xs"
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy script
                  </Button>
                  <a
                    href="https://www.sap.com/products/technology-platform/sql-anywhere.html"
                    target="_blank" rel="noreferrer"
                    className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    Get SQL Anywhere Developer <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}

            {detection.extractable && !extractResult && (
              <div className="pt-2">
                <Button
                  onClick={doExtract}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="legacy-extract-btn"
                >
                  {busy
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Extracting…</>
                    : <><Database className="w-4 h-4 mr-1.5" /> Auto-extract tables → CSV</>}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3 — Extract result */}
        {extractResult && (
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 space-y-2" data-testid="extract-result">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
              <span className="text-sm font-semibold text-emerald-900">
                Extracted {extractResult.total_files} table(s) · {extractResult.total_rows.toLocaleString()} rows
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs" data-testid="extract-tables-table">
                <thead className="bg-emerald-100/50">
                  <tr>
                    <th className="text-left px-2 py-1 border border-emerald-200 font-semibold text-emerald-900">Table</th>
                    <th className="text-right px-2 py-1 border border-emerald-200 font-semibold text-emerald-900">Rows</th>
                    <th className="text-left px-2 py-1 border border-emerald-200 font-semibold text-emerald-900">Output file</th>
                  </tr>
                </thead>
                <tbody>
                  {extractResult.tables.map((t) => (
                    <tr key={t.table_name} className="hover:bg-white/50">
                      <td className="px-2 py-1 border border-emerald-200 font-medium text-emerald-900">{t.table_name}</td>
                      <td className="px-2 py-1 border border-emerald-200 text-right text-emerald-900">{t.row_count.toLocaleString()}</td>
                      <td className="px-2 py-1 border border-emerald-200 text-emerald-800"><code className="text-[10px]">{t.file_name}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {activeProject ? (
              <Button
                onClick={importIntoProject}
                disabled={busy}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="legacy-import-btn"
              >
                {busy
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</>
                  : <>Register into project <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            ) : (
              <div className="text-[11px] text-emerald-800 flex items-center gap-1">
                <FileWarning className="w-3.5 h-3.5" /> Create / select a Migration Project first to auto-register these CSVs as data sources.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
